import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useParams, useSearchParams } from "react-router-dom";
import { loadDb, setTrackReaction, Track, Playlist, fetchPlaylists } from "../data/schema";
import { generateOrganicPattern } from "../../../utils/patternGenerator";
import LikeIcon from "../../../assets/like.svg";
import DislikeIcon from "../../../assets/dislike.svg";
import ExportIcon from "../../../assets/export.svg";
import EditIcon from "../../../assets/edit-ws.svg";
import TrashIcon from "../../../assets/trash.svg";
import { TrackModal } from "../RightPanel/TrackModal";
import { TrackMenu } from "../RightPanel/TrackMenu";
import { AddToPlaylistModal } from "../RightPanel/Modals/AddToPlaylistModal";
import { MoveToWorkspaceModal } from "../RightPanel/Modals/MoveToWorkspaceModal";
import { ReportBugModal } from "../RightPanel/Modals/ReportBugModal";
import { ShareModal } from "../RightPanel/Modals/ShareModal";
import { EditPlaylistModal } from "../RightPanel/Modals/EditPlaylistModal";
import { MusicPlayer, MusicPlayerHandle } from "../RightPanel/MusicPlayer";
import { clearQueue } from "../data/queue";
import { showToast } from "components/ui-kit/Toast";
import PlayIcon from "../../../assets/play-player.svg";
import { CreatePersonaModal } from "components/AICompose/RightPanel/Modals/CreatePersonaModal";
import { AUDIO_LOAD_START_EVENT, AUDIO_LOAD_END_EVENT } from "../events";
import { ProgressSpinner } from "primereact/progressspinner";

export const PlaylistView: React.FC = () => {
  // UUID плейлиста из URL или query-параметра
  const { uuid: playlistUuidParam } = useParams<{ uuid?: string }>();
  const [searchParams] = useSearchParams();
  const playlistSlug = playlistUuidParam || searchParams.get('uuid') || searchParams.get('playlistId');
  const [playingTrack, setPlayingTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<MusicPlayerHandle | null>(null);
  const hasPlayer = !!playingTrack;
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [loading, setLoading] = useState(true);
  const [editPlaylistOpen, setEditPlaylistOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [submenuOpen, setSubmenuOpen] = useState<'share' | 'download' | 'report' | null>(null);
  const [addToPlaylistTrack, setAddToPlaylistTrack] = useState<Track | null>(null);
  const [moveToWorkspaceTrack, setMoveToWorkspaceTrack] = useState<Track | null>(null);
  const [reportBugTrack, setReportBugTrack] = useState<Track | null>(null);
  const [shareTrack, setShareTrack] = useState<Track | null>(null);
  const [createPersonaTrack, setCreatePersonaTrack] = useState<Track | null>(null);
  const [audioLoadingTrackIds, setAudioLoadingTrackIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set<string>();
    const anyWindow = window as any;
    const globalSet: Set<string> | undefined = anyWindow.__audioLoadingTrackIds;
    return globalSet ? new Set(globalSet) : new Set<string>();
  });

  // Load playlist by UUID (или по старому id, если uuid нет)
  useEffect(() => {
    const loadPlaylist = async () => {
      if (!playlistSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch playlists from server to ensure we have latest data
        await fetchPlaylists();
        
        // Load from local database
        const db = loadDb();
        // Сначала пробуем найти по uuid, затем по id (для совместимости со старыми ссылками)
        const foundPlaylist =
          Object.values(db.playlists).find((p) => p.uuid === playlistSlug) ||
          db.playlists[playlistSlug];
        
        if (!foundPlaylist) {
          showToast('Playlist not found', undefined, 'error');
          setLoading(false);
          return;
        }
        
        setPlaylist(foundPlaylist);
      } catch (error) {
        console.error('Failed to load playlist:', error);
        showToast('Failed to load playlist', undefined, 'error');
      } finally {
        setLoading(false);
      }
    };

    loadPlaylist();
  }, [playlistSlug]);

  // Listen for track creation events
  useEffect(() => {
    const handleTrackCreated = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackCreated', handleTrackCreated);
    return () => window.removeEventListener('trackCreated', handleTrackCreated);
  }, []);
  
  // Listen for track reaction changes
  useEffect(() => {
    const handleReactionChanged = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackReactionChanged', handleReactionChanged);
    return () => window.removeEventListener('trackReactionChanged', handleReactionChanged);
  }, []);

  useEffect(() => {
    const handleAudioLoadStart = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds(prev => {
        const next = new Set(prev);
        next.add(detail.trackId);
        return next;
      });
    };
    const handleAudioLoadEnd = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds(prev => {
        if (!prev.has(detail.trackId)) return prev;
        const next = new Set(prev);
        next.delete(detail.trackId);
        return next;
      });
    };
    window.addEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
    window.addEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    return () => {
      window.removeEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
      window.removeEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    };
  }, []);

  const isTrackLoading = useCallback(
    (track: Track) => Boolean(track?.params?.isLoading) || audioLoadingTrackIds.has(track.id),
    [audioLoadingTrackIds]
  );

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-track-menu]')) {
        setMenuOpenId(null);
        setSubmenuOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);

  const handlePlayerToggle = () => {
    if (playerRef.current) {
      playerRef.current.togglePlay();
    }
  };

  const [sourceTracksForPlayer, setSourceTracksForPlayer] = useState<Track[]>([]);

  const handlePlayTrack = (track: Track | null) => {
    if (!track) {
      setPlayingTrack(null);
      setSourceTracksForPlayer([]);
      return;
    }
    
    clearQueue();
    
    if (playlist) {
      setSourceTracksForPlayer(playlistTracks);
    }
    
    setPlayingTrack(track);
  };

  // Get tracks for playlist
  const playlistTracks = useMemo(() => {
    if (!playlist) return [];
    const db = loadDb();
    return playlist.trackIds
      .map(id => db.tracks[id])
      .filter(Boolean)
      .filter(t => !t.trashed)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [playlist, refreshKey]);

  const handlePlayPlaylist = () => {
    if (!playlist || playlistTracks.length === 0) return;
    clearQueue();
    setSourceTracksForPlayer(playlistTracks);
    const firstTrack = playlistTracks[0];
    setPlayingTrack(firstTrack);
  };

  const PLAYLIST_GRADIENTS = [
    'linear-gradient(135deg, #667eea, #764ba2)',
    'linear-gradient(135deg, #f093fb, #f5576c)',
    'linear-gradient(135deg, #4facfe, #00f2fe)',
    'linear-gradient(135deg, #43e97b, #38f9d7)',
    'linear-gradient(135deg, #fa709a, #fee140)',
    'linear-gradient(135deg, #30cfd0, #330867)',
  ];

  const getGradientForPlaylist = (id: string): string => {
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return PLAYLIST_GRADIENTS[index % PLAYLIST_GRADIENTS.length];
  };

  const getGradientFromKey = (key: string): string => {
    const indexStr = key.split(':')[1] ?? '0';
    const index = parseInt(indexStr, 10);
    if (Number.isNaN(index)) return PLAYLIST_GRADIENTS[0];
    return PLAYLIST_GRADIENTS[((index % PLAYLIST_GRADIENTS.length) + PLAYLIST_GRADIENTS.length) % PLAYLIST_GRADIENTS.length];
  };

  if (loading) {
    return (
      <Container $hasPlayer={hasPlayer}>
        <Loader>
          <ProgressSpinner />
        </Loader>
      </Container>
    );
  }

  if (!playlist) {
    return (
      <Container $hasPlayer={hasPlayer}>
        <ErrorMessage>Playlist not found</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container $hasPlayer={hasPlayer}>
      <ContentWrapper $hasModal={!!selectedTrack} $hasPlayer={hasPlayer}>
        <PlaylistViewContainer>
          <PlaylistHeaderRow>
            <PlaylistHeader>
              <PlaylistCover>
                {playlist.id === 'playlist_liked_songs' ? (
                  <PlaylistCoverImage>
                    <LikeIconWrapper>
                      <LikeIcon color="#fff" width="20px" height="20px" />
                    </LikeIconWrapper>
                  </PlaylistCoverImage>
                ) : playlist.coverImage?.startsWith('gradient:') ? (
                  <PlaylistCoverImage $gradient={getGradientFromKey(playlist.coverImage)} />
                ) : playlist.coverImage ? (
                  <PlaylistCoverImageImg src={playlist.coverImage} alt={playlist.name} />
                ) : (
                  <PlaylistCoverImage $gradient={getGradientForPlaylist(playlist.id)} />
                )}
              </PlaylistCover>
              <PlaylistHeaderInfo>
                <PlaylistTitleRow>
                  <PlaylistViewTitle>{playlist.name}</PlaylistViewTitle>
                  {/* {playlist.id !== 'playlist_liked_songs' && (
                    <PlaylistActionsWrapper>
                      <EditPlaylistButton onClick={() => setEditPlaylistOpen(true)}>
                        <EditIcon />
                      </EditPlaylistButton>
                    </PlaylistActionsWrapper>
                  )} */}
                </PlaylistTitleRow>
                <PlaylistHeaderActions>
                  <PlayButton onClick={handlePlayPlaylist} disabled={playlistTracks.length === 0}>
                    <PlayButtonText>Play playlist</PlayButtonText>
                    <PlayIcon />
                  </PlayButton>
                </PlaylistHeaderActions>
              </PlaylistHeaderInfo>
            </PlaylistHeader>
          </PlaylistHeaderRow>
          {playlistTracks.length === 0 ? (
            <EmptyPlaylistState>
              <EmptyPlaylistText>
                Start your playlist by creating a song or adding an existing song from your Library
              </EmptyPlaylistText>
            </EmptyPlaylistState>
          ) : (
            <PlaylistTrackList>
              {playlistTracks.map((track: Track) => {
                const mode = track?.params?.mode || 'easy';
                let subtitleText = '';
                
                if (mode === 'easy') {
                  const prompt: string = (track?.params?.songDescription || '').toString();
                  subtitleText = prompt;
                } else {
                  const styles: string = (track?.params?.stylesDescription || '').toString();
                  subtitleText = styles || '(No styles)';
                }
                
                const maxLength = selectedTrack ? 40 : 100;
                const subtitleShort = subtitleText ? (subtitleText.length > maxLength ? `${subtitleText.slice(0, maxLength)}...` : subtitleText) : '';
                const reaction = track.reaction || null;
                const loading = isTrackLoading(track);
                const playableUrl = track.params?.localAudioUrl || null;
                const artworkUrl =
                  track.params?.localImageUrl ||
                  track.params?.imageUrl ||
                  null;
                
                return (
                  <TrackItem key={track.id}>
                    <CheckboxWrapper>
                      <Checkbox 
                        $visible={selectedTracks.has(track.id)}
                        type="checkbox"
                        checked={selectedTracks.has(track.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedTracks);
                          if (e.target.checked) {
                            newSelected.add(track.id);
                          } else {
                            newSelected.delete(track.id);
                          }
                          setSelectedTracks(newSelected);
                        }}
                      />
                    </CheckboxWrapper>
                    <ThumbnailWrapper
                      $isLoading={loading}
                      onClick={() => {
                        if (loading) return;
                        if (!playableUrl) {
                          showToast("Audio is not available yet. Please wait a bit longer.");
                          return;
                        }
                        const isThisTrack = playingTrack?.id === track.id;
                        if (isThisTrack) {
                          handlePlayerToggle();
                        } else {
                          handlePlayTrack(track);
                        }
                      }}
                    >
                      <Thumbnail $imageUrl={artworkUrl} />
                      <PlayOverlay $isLoading={loading}>
                        {loading ? (
                          <LoaderSpinner />
                        ) : playingTrack?.id === track.id && isPlaying ? (
                          <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: '#6777FF'}}>
                            <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                            <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
                          </svg>
                        ) : (
                          <PlayIcon />
                        )}
                      </PlayOverlay>
                      {track.durationSec && !loading && (
                        <DurationBadge>
                          {`${Math.floor(track.durationSec / 60)}:${Math.round(track.durationSec % 60).toString().padStart(2, '0')}`}
                        </DurationBadge>
                      )}
                    </ThumbnailWrapper>
                    <TrackInfo onClick={() => {
                      if (loading) return;
                      setSelectedTrack(track);
                    }}>
                      <TrackTitle>
                        {track.title || 'Song'}
                      </TrackTitle>
                      <TrackSubtitle>{subtitleShort}</TrackSubtitle>
                      <TrackActions onClick={(e) => e.stopPropagation()}>
                        <ReactionButton
                          title="Like"
                          $active={reaction === 'like'}
                          disabled={loading}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (loading) return;
                            const next = reaction === 'like' ? null : 'like'; 
                            try {
                              await setTrackReaction(track.id, next as any);
                            } catch (error) {
                              console.error('Failed to set track reaction:', error);
                              showToast('Failed to update reaction', undefined, 'error');
                            }
                          }}
                        >
                          <LikeIcon />
                        </ReactionButton>
                        <ReactionButton
                          title="Dislike"
                          $active={reaction === 'dislike'}
                          disabled={loading}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (loading) return;
                            const next = reaction === 'dislike' ? null : 'dislike'; 
                            try {
                              await setTrackReaction(track.id, next as any);
                            } catch (error) {
                              console.error('Failed to set track reaction:', error);
                              showToast('Failed to update reaction', undefined, 'error');
                            }
                          }}
                        >
                          <DislikeIcon />
                        </ReactionButton>
                        <ActionButton 
                          title="Export"
                          disabled={loading}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (loading) return;
                            setShareTrack(track);
                          }}
                        >
                          <ExportIcon />
                        </ActionButton>
                      </TrackActions>
                    </TrackInfo>
                    {/* <MoreWrapper onClick={(e) => e.stopPropagation()} data-track-menu>
                      {!loading && (
                        <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === track.id ? null : track.id); setSubmenuOpen(null); }}>⋯</MoreButton>
                      )}
                      {!loading && menuOpenId === track.id && (
                        <TrackMenu
                          trackId={track.id}
                          track={track}
                          onClose={() => { setMenuOpenId(null); setSubmenuOpen(null); }}
                          onAddToPlaylist={() => { setAddToPlaylistTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                          onMoveToWorkspace={() => { setMoveToWorkspaceTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                          onCreatePersona={() => { setCreatePersonaTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                          onAddToQueue={() => {}}
                          onMoveToTrash={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
                          onReportBug={() => { 
                            setReportBugTrack(track); 
                          }}
                          onReportInappropriate={() => { 
                            showToast("Marked, thanks!", undefined, 'success');
                          }}
                          onShareTo={() => { 
                            setShareTrack(track); 
                          }}
                          selectedTracks={selectedTracks}
                          submenuOpen={submenuOpen}
                          onSubmenuOpen={setSubmenuOpen}
                        />
                      )}
                    </MoreWrapper> */}
                  </TrackItem>
                );
              })}
            </PlaylistTrackList>
          )}
        </PlaylistViewContainer>
      </ContentWrapper>
      {selectedTrack && (
        <TrackModal
          track={selectedTrack}
          onClose={() => setSelectedTrack(null)}
          hasPlayer={hasPlayer}
          onPlayTrack={handlePlayTrack}
          playingTrack={playingTrack}
          isPlaying={isPlaying}
          onTogglePlay={handlePlayerToggle}
          source="library"
        />
      )}
      {hasPlayer && (
        <MusicPlayer
          ref={playerRef}
          track={playingTrack}
          sourceTracks={sourceTracksForPlayer}
          onTrackChange={setPlayingTrack}
          onTogglePlay={(isPlaying) => setIsPlaying(isPlaying)}
          onClose={() => {
            setPlayingTrack(null);
            setSourceTracksForPlayer([]);
          }}
        />
      )}
      {addToPlaylistTrack && (
        <AddToPlaylistModal
          open={!!addToPlaylistTrack}
          track={addToPlaylistTrack}
          onClose={() => setAddToPlaylistTrack(null)}
          onAdded={async () => {
            await fetchPlaylists();
            setRefreshKey(k => k + 1);
          }}
        />
      )}
      {moveToWorkspaceTrack && (
        <MoveToWorkspaceModal
          open={!!moveToWorkspaceTrack}
          track={moveToWorkspaceTrack}
          onClose={() => setMoveToWorkspaceTrack(null)}
          onMoved={() => {
            setRefreshKey(k => k + 1);
          }}
        />
      )}
      {reportBugTrack && (
        <ReportBugModal
          open={!!reportBugTrack}
          track={reportBugTrack}
          onClose={() => setReportBugTrack(null)}
        />
      )}
      {shareTrack && (
        <ShareModal
          open={!!shareTrack}
          track={shareTrack}
          onClose={() => setShareTrack(null)}
        />
      )}
      {editPlaylistOpen && (
        <EditPlaylistModal
          open={editPlaylistOpen}
          playlist={playlist}
          onClose={() => {
            setEditPlaylistOpen(false);
            setRefreshKey(k => k + 1);
          }}
          onSaved={async () => {
            await fetchPlaylists();
            const db = loadDb();
            const updatedPlaylist = db.playlists[playlist.id];
            if (updatedPlaylist) {
              setPlaylist(updatedPlaylist);
            }
            setRefreshKey(k => k + 1);
          }}
        />
      )}
      {createPersonaTrack && (
        <CreatePersonaModal
          open={!!createPersonaTrack}
          track={createPersonaTrack}
          onClose={() => setCreatePersonaTrack(null)}
        />
      )}
    </Container>
  );
};

// Styled components - copied from Library/index.tsx
const Container = styled.div<{ $hasPlayer?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.palette.background.primary || '#fff'};
  overflow: hidden;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
`;

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  color: #666;
  font-size: 16px;
`;

const ContentWrapper = styled.div<{ $hasModal: boolean; $hasPlayer?: boolean }>`
  width: ${p => p.$hasModal ? 'calc(70% - 10px)' : '100%'};
  flex: 1;
  min-height: 0;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  padding-bottom: ${p => p.$hasPlayer ? '120px' : '20px'};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PlaylistViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 0;
  position: relative;
`;

const PlaylistHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const PlaylistHeader = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex: 1;
  margin-top: 0;
`;

const PlaylistCover = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
`;

const PlaylistCoverImage = styled.div<{ $gradient?: string }>`
  width: 100%;
  height: 100%;
  background: ${p => p.$gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistCoverImageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LikeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistHeaderInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;
`;

const PlaylistTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const PlaylistViewTitle = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #000;
  font-family: serif;
`;

const PlaylistActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditPlaylistButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: transparent;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PlaylistHeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const PlayButton = styled.button<{ disabled?: boolean }>`
  height: 56px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f4f4f4;
    border-color: #ccc;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const PlayButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
`;

const EmptyPlaylistState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyPlaylistText = styled.div`
  color: #000;
  font-size: 18px;
  max-width: 80%;
`;

const PlaylistTrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Checkbox = styled.input<{ $visible?: boolean }>`
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: ${p => p.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
`;

const Thumbnail = styled.div<{ $imageUrl?: string }>`
  width: 7.5vh;
  height: 9vh;
  border-radius: 6px;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
  transition: opacity 0.15s ease;
`;

const PlayOverlay = styled.div<{ $isLoading?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: ${p => p.$isLoading ? 1 : 0};
  transition: opacity 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ThumbnailWrapper = styled.div<{ $isLoading?: boolean }>`
  position: relative;
  cursor: ${p => p.$isLoading ? 'default' : 'pointer'};
  flex-shrink: 0;
  &:hover {
    ${Thumbnail} {
      opacity: ${p => p.$isLoading ? 1 : 0.7};
    }
    ${PlayOverlay} {
      opacity: 1;
    }
  }
`;

const DurationBadge = styled.div`
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
`;

const TrackItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  margin: 3px 10px;

  &:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    ${Checkbox} {
      opacity: 1;
    }
  }
  ${Checkbox}[checked] {
    opacity: 1 !important;
  }
`;

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  cursor: pointer;
`;

const TrackTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const TrackSubtitle = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const TrackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ReactionButton = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${p => p.$active ? '#6777FF' : '#9aa0a6'};
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { color: #6777FF; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6777FF;
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { opacity: 1; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const MoreWrapper = styled.div`
  margin-left: auto;
  position: relative;
`;

const MoreButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  border: 1px solid #e9ecef;
  background: #fff;
  cursor: pointer;
`;

const LoaderSpinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(103, 119, 255, 0.2);
  border-top-color: #6777FF;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;